// UI2 registers this source once. The approval token and scope selection stay
// inside the source, outside presentation records and never in local storage.
import { z } from 'zod';

const BASE = '/api/v1/company/agent-access/coding';
const nullableText = z.string().nullable();
const descriptor = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  family: z.string(),
});
const grant = z.object({
  grantId: z.string(),
  clientName: z.string(),
  kind: z.enum(['oauth', 'access_key']),
  scopes: z.array(descriptor),
  ownerName: nullableText,
  lastSeenAt: nullableText,
  seenState: z.enum(['recent', 'stale', 'never']).nullable(),
  state: z.enum(['active', 'revoked', 'expired']),
  pendingConsentRequestId: nullableText,
  originLabel: nullableText,
});
const vm = z
  .object({ canAdminister: z.boolean(), mine: z.array(grant), company: z.array(grant).nullable() })
  .refine(({ canAdminister, company }) => canAdminister || company === null);
const consent = z.object({
  requestId: z.string(),
  clientName: z.string(),
  companyName: z.string(),
  status: z.enum(['requested', 'refused', 'consented', 'revoked', 'stale']),
  refusalReason: nullableText,
  scopes: z.array(descriptor.extend({ withinRights: z.boolean() })),
  alreadyGranted: z.array(z.string()),
  agentOrigin: nullableText.optional(),
  vendorLabel: nullableText.optional(),
  payerName: nullableText.optional(),
  dataLeavesEu: z.boolean().nullable().optional(),
  lifetimeDays: z.number().positive().nullable().optional(),
});
const approval = z.object({
  csrf: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  scopes: z.array(z.string()),
});

async function browserRequest(path, { method = 'GET', body } = {}) {
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    cache: 'no-store',
    ...(body
      ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }
      : {}),
  });
  return { status: response.status, value: await response.json() };
}

function loaded(response, schema) {
  if (response.status !== 200) return { status: 'error' };
  if (response.value?.status === 'unavailable') return { status: 'unavailable' };
  if (response.value?.status !== 'ready') return { status: 'error' };
  const parsed = schema.safeParse(response.value.value);
  return parsed.success ? { status: 'ready', value: parsed.data } : { status: 'error' };
}

const requestPath = (id) => `${BASE}/requests/${encodeURIComponent(id)}`;
const redirect = z.string().refine((value) => {
  try {
    return !['javascript:', 'data:', 'file:', 'vbscript:'].includes(new URL(value).protocol);
  } catch {
    return false;
  }
});

// The card restates the complete displayed grant; an optional checkbox client
// may select a subset. Both send only explicitly reviewed, currently offered
// scopes. Deny approves none and cannot mutate an existing grant.
function approvedChoice(bound, decision, scopes) {
  if (!Array.isArray(scopes) || new Set(scopes).size !== scopes.length) return false;
  if (decision === 'deny') return scopes.length === 0;
  return (
    decision === 'allow' &&
    scopes.includes('knowledge:read') &&
    scopes.every((id) => bound.scopes.includes(id))
  );
}

export function createCodingAgentsHttpSource({
  request = browserRequest,
  navigate = (url) => globalThis.location.assign(url),
} = {}) {
  const approvals = new Map();
  return {
    async list() {
      try {
        return loaded(await request(BASE), vm);
      } catch {
        return { status: 'error' };
      }
    },
    async loadRequest(requestId) {
      approvals.delete(requestId);
      try {
        const response = await request(requestPath(requestId));
        const value = loaded(response, consent);
        if (value.status !== 'ready' || value.value.requestId !== requestId)
          return value.status === 'ready' ? { status: 'error' } : value;
        if (value.value.status === 'requested') {
          const parsed = approval.safeParse(response.value.approval);
          const reviewed = value.value.scopes
            .filter(({ withinRights }) => withinRights)
            .map(({ id }) => id);
          if (
            !parsed.success ||
            parsed.data.scopes.length !== reviewed.length ||
            parsed.data.scopes.some((id, index) => id !== reviewed[index])
          )
            return { status: 'error' };
          if (approvals.size >= 64) approvals.delete(approvals.keys().next().value);
          approvals.set(requestId, parsed.data);
        }
        return value;
      } catch {
        return { status: 'error' };
      }
    },
    async decide(requestId, decision, approvedScopeIds) {
      const bound = approvals.get(requestId);
      if (!bound || !approvedChoice(bound, decision, approvedScopeIds)) return 'failed';
      // A failed/lost response cannot authorize a second send with this token.
      approvals.delete(requestId);
      try {
        const response = await request(`${requestPath(requestId)}/decision`, {
          method: 'POST',
          body: { decision, csrf: bound.csrf, scopes: [...approvedScopeIds] },
        });
        if (
          response.value?.error === 'consent_rights_changed' ||
          response.value?.error === 'company_required'
        )
          return 'stale';
        if (response.value?.error === 'consent_refused') return 'refused';
        if (response.status !== 200 || response.value?.result !== 'done') return 'failed';
        const target = redirect.safeParse(response.value.redirectTo);
        if (!target.success) return 'failed';
        navigate(target.data);
        return 'done';
      } catch {
        return 'failed';
      }
    },
    async revoke(grantId) {
      try {
        const response = await request(`${BASE}/grants/${encodeURIComponent(grantId)}`, {
          method: 'DELETE',
        });
        if (response.status === 403) return 'denied';
        if (response.status === 404) return 'gone';
        return response.status === 200 &&
          ['done', 'denied', 'gone'].includes(response.value?.result)
          ? response.value.result
          : 'failed';
      } catch {
        return 'failed';
      }
    },
    async requestStatus(requestId) {
      try {
        const response = await request(`${requestPath(requestId)}/status`);
        if (response.status !== 200) return { state: 'unknown' };
        const value = response.value;
        if (value?.state === 'applied' && ['allow', 'deny'].includes(value.settled))
          return { state: 'applied', settled: value.settled };
        if (['not_applied', 'pending'].includes(value?.state)) return { state: value.state };
        return { state: 'unknown' };
      } catch {
        return { state: 'unknown' };
      }
    },
  };
}
